/**
 * Thin fetch wrapper for the JSON API.
 *
 * Everything goes through the Worker on the same origin, so there is no CORS
 * layer and no SDK -- the original pulled five Firebase compat bundles
 * (~350 KB) from gstatic before the page could render anything.
 */

import type { Fixture, GroupNode, League, TeamLibraryEntry } from '../../shared/types';
import { normalizeLeague } from '../../shared/sports';

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    throw new ApiError(
      response.status,
      body.error ?? 'unknown',
      body.message ?? `Request failed (${response.status})`,
    );
  }
  return (await response.json()) as T;
}

export interface GameSummary {
  id: string;
  title: string;
  home_team: string;
  away_team: string;
  kickoff_at: string | null;
  mode: 'standard' | 'everyscore';
  status: 'setup' | 'open' | 'live' | 'final' | 'archived';
  square_cost: number;
  updated_at: string;
  /** Season the matchup belongs to, in its sport's own numbering. */
  season: number | null;
  /** League of the matchup, or null for a hand-made game. */
  league: string | null;
  /** ISO timestamp when shelved, or null while active. */
  archived_at: string | null;
  role: 'admin' | 'player' | 'spectator';
  last_seen_at: string | null;
}

export const api = {
  /** Active games by default; `archived` fetches the shelf instead. */
  listGames: (archived = false) =>
    request<{ games: GameSummary[]; archivedCount: number }>(
      `/api/games${archived ? '?archived=1' : ''}`,
    ),

  setArchived: (id: string, archived: boolean) =>
    request<{ id: string; archived: boolean }>(
      `/api/games/${encodeURIComponent(id)}/archive`,
      { method: 'POST', body: JSON.stringify({ archived }) },
    ),

  createGame: (body: {
    title?: string;
    customId?: string;
    /** A fixture picked in the wizard; the server resolves the matchup. */
    from?: { teamId: string; eventId: string };
  }) =>
    request<{ id: string; title: string }>('/api/games', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Conference/division tree for one league, straight from our library. */
  groups: (league: League) =>
    request<{ league: League; groups: GroupNode[] }>(
      `/api/teams/groups?league=${league}`,
    ),

  /** A team's fixture list, proxied and cached through the Worker. */
  schedule: (teamId: string) =>
    request<{
      team: string;
      season: number | null;
      fixtures: Fixture[];
      stale?: boolean;
      /** True once the regular season is exhausted and the bracket was looked up. */
      postseasonChecked?: boolean;
    }>(
      `/api/teams/${encodeURIComponent(teamId)}/schedule`,
    ),

  /** Who the caller is, and whether they own this deployment. */
  me: () => request<{ user: { id: string; email: string } | null; isOwner: boolean }>('/api/me'),

  deleteGame: (id: string) =>
    request<{ deleted: string }>(`/api/games/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  searchTeams: (params: {
    q?: string;
    league?: string;
    conference?: string;
    division?: string;
    limit?: string;
  }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, value);
    }
    return request<{ teams: TeamRow[] }>(`/api/teams?${query}`);
  },

  conferences: () =>
    request<{ conferences: { league: string; conference: string; team_count: number }[] }>(
      '/api/teams/conferences',
    ),

  markTeamUsed: (id: string) =>
    request<{ ok: true }>(`/api/teams/${encodeURIComponent(id)}/use`, { method: 'POST' }),

  /**
   * Owner only. Rebuilds whichever league is most overdue, or moves a slice of
   * the logo backlog when none is. One league per call -- see the note on
   * subrequest budget in server/routes/sync-teams.ts.
   */
  syncTeams: () =>
    request<{
      league: string | null;
      teams: number;
      error: string | null;
      moreDue: boolean;
      logosMirrored: number;
      logosRemaining: number | null;
    }>('/api/teams/sync', { method: 'POST' }),

  uploadLogo: (teamId: string, file: File) => {
    const form = new FormData();
    form.set('logo', file);
    return request<{ key: string; url: string }>(
      `/api/teams/${encodeURIComponent(teamId)}/logo`,
      { method: 'POST', body: form },
    );
  },
};

/** Row shape as it comes back from D1 (snake_case columns). */
export interface TeamRow {
  id: string;
  external_id: number | null;
  name: string;
  mascot: string;
  abbr: string;
  conference: string;
  division: string;
  classification: string;
  league: string;
  logo_url: string;
  logo_key: string | null;
  primary_color: string;
  usage_count: number;
}

export function toTeamEntry(row: TeamRow): TeamLibraryEntry {
  return {
    id: row.id,
    externalId: row.external_id,
    name: row.name,
    mascot: row.mascot,
    abbr: row.abbr,
    conference: row.conference,
    classification: row.classification,
    league: normalizeLeague(row.league),
    logoUrl: row.logo_url,
    primaryColor: row.primary_color,
    usageCount: row.usage_count,
  };
}
